import React, { ReactNode, useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  width?: string;
  showCloseButton?: boolean;
  closeOnClickOutside?: boolean;
  closeOnEsc?: boolean;
}

const ModalMod: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  width = "600px",
  showCloseButton = true,
  closeOnClickOutside = true,
  closeOnEsc = true,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  // Track mount state for animations
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      // Small delay to ensure mount happened before animation
      requestAnimationFrame(() => {
        setIsAnimating(true);
      });
    } else {
      setIsAnimating(false);
      // Wait for animation to complete before unmounting
      const timer = setTimeout(() => {
        setIsMounted(false);
      }, 200); // Match this to your CSS transition duration
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Prevent body scrolling
  useEffect(() => {
    if (isMounted) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMounted]);

  if (!isMounted) return null;

  return (
    <div className="fixed inset-0 zIndex flex items-center justify-center"
     style={{zIndex:"9999"}}>
      {/* Overlay with fade effect */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-200  ease-in-out ${
          isAnimating ? "opacity-50" : "opacity-0"
        }`} 
       
      />

      {/* Modal Content with slide-up effect */}
      <div
        ref={modalRef}
        className={`relative  zIndex bg-white rounded-2xl shadow-xl  flex flex-col overflow-hidden transition-all duration-200 ease-in-out ${
          isAnimating ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        }`}
        style={{ width }}
      >
        {/* Modal Header */}
        {(title || showCloseButton) && (
          <div className="flex items-center justify-start w-full px-6 py-2 border border-[#DFEAF2] border-rounded-2xl">
            {title && (
              <div>
                <h5 className="text-[20px] text-[#525062] font-medium w-full">
                  {title}
                </h5>
                <p className="text-center text-[#525062] text-[13px] mt-1 font-medium">
                  GNA Energy Data and Analytics Platform (GNAi)
                </p>
              </div>
            )}
            {showCloseButton && (
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 focus:outline-none absolute top-4 right-4"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            )}
          </div>
        )}

        {/* Modal Body */}
        <div className="p-3 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
};

export default ModalMod;
